import React, { useState } from 'react';

import { ProductSearch } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch';
import { VariationDetails } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/ProductSearch/VariationDetails';
import { MaterialGroups } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/WallMenu/MaterialGroups';
import { useInitialFilter } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/WallMenu/useInitialFilter';
import { useWallMaterialGroups } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/WallMenu/useWallMaterialGroups';
import { useTrackProductSelectionEvent } from 'src/modules/designStudio/components/DesignMenu/ProductCatalogMenu/useTrackProductSelectionEvent';
import { useBuildingDesigner } from 'src/modules/designStudio/helpers/machete/useBuildingDesigner';
import type {
  MaterialGroup,
  Variation,
} from 'src/modules/designStudio/helpers/productSelection/types';
import { ProductCategory } from 'src/modules/designStudio/helpers/productSelection/types';

enum ActiveView {
  VIEW_GROUP_DETAILS,
  VIEW_MATERIAL_GROUPS,
  SEARCH_PRODUCTS,
}

export const WallMenu = ({
  closeMenu,
  headerText,
}: {
  headerText: string | null;
  closeMenu: () => void;
}) => {
  const { mutateBuilding } = useBuildingDesigner();
  const wallMaterialGroups = useWallMaterialGroups();

  const [viewMode, setViewMode] = useState<ActiveView>(
    ActiveView.VIEW_MATERIAL_GROUPS,
  );

  const [selectedMaterialGroup, setSelectedMaterialGroup] = useState<
    MaterialGroup | undefined
  >(undefined);

  const [activeVariation, setActiveVariation] = useState<Variation | undefined>(
    undefined,
  );
  const initialFilter = useInitialFilter({ wallGroup: selectedMaterialGroup });

  const { trackRealProductSelectionEvent } = useTrackProductSelectionEvent({
    category: ProductCategory.WALLS,
  });

  const actionsByView: Record<
    ActiveView,
    Record<string, (...args: any) => void>
  > = {
    [ActiveView.VIEW_MATERIAL_GROUPS]: {
      onPrimaryClick: (group: MaterialGroup) => {
        trackRealProductSelectionEvent(
          {
            track_id: 'product-selection-wallgroup-edit',
            button_text: 'Edit',
          },
          group.variation,
        );

        setViewMode(ActiveView.SEARCH_PRODUCTS);
        setSelectedMaterialGroup(group);
      },
      onSecondaryClick: (group: MaterialGroup) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-wallgroup-more`,
          },
          group.variation,
        );

        setViewMode(ActiveView.VIEW_GROUP_DETAILS);
        setSelectedMaterialGroup(group);
        setActiveVariation(group.variation);
      },
    },
    [ActiveView.SEARCH_PRODUCTS]: {
      onBack: () => {
        if (wallMaterialGroups.length > 1) {
          setViewMode(ActiveView.VIEW_MATERIAL_GROUPS);
        } else {
          /**
           * if there is only one material group and the user presses back in the product search
           * we simply close the menu.
           */
          closeMenu();
        }
      },
      onVariationSelected: (variation: Variation) => {
        if (selectedMaterialGroup) {
          mutateBuilding(async mutations => {
            const newAppearance = {
              textureId: variation.textureId,
              color: variation.color?.hex,
              variationUUID: variation.uuid,
            };
            await mutations.changeWallAppearances(
              [selectedMaterialGroup.appearance],
              newAppearance,
            );
            setSelectedMaterialGroup({
              appearance: newAppearance,
              variation,
            });
          });
        }
      },
    },
    [ActiveView.VIEW_GROUP_DETAILS]: {
      onBack: () => {
        setViewMode(ActiveView.VIEW_MATERIAL_GROUPS);
      },
      onDifferentSizeSelected: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${ProductCategory.WALLS}-size-select-click`,
          },
          variation,
        );

        setActiveVariation(variation);
      },
      onDifferentColorSelected: (variation: Variation) => {
        trackRealProductSelectionEvent(
          {
            track_id: `product-selection-${ProductCategory.WALLS}-color-select-click`,
          },
          variation,
        );

        setActiveVariation(variation);
      },
      onApply: () => {
        if (selectedMaterialGroup && activeVariation) {
          mutateBuilding(async mutations => {
            const newAppearance = {
              textureId: activeVariation.textureId,
              color: activeVariation.color?.hex,
              variationUUID: activeVariation.uuid,
            };
            await mutations.changeWallAppearances(
              [selectedMaterialGroup.appearance],
              newAppearance,
            );
            setSelectedMaterialGroup({
              appearance: newAppearance,
              variation: activeVariation,
            });
          });
        }
      },
    },
  };

  const viewByMode: Record<ActiveView, React.ReactElement> = {
    [ActiveView.VIEW_MATERIAL_GROUPS]: (() => {
      const actions = actionsByView[ActiveView.VIEW_MATERIAL_GROUPS];
      return (
        <MaterialGroups
          materialGroups={wallMaterialGroups}
          onBack={closeMenu}
          onPrimaryClick={actions.onPrimaryClick}
          onSecondaryClick={actions.onSecondaryClick}
        />
      );
    })(),
    [ActiveView.SEARCH_PRODUCTS]: (() => {
      const actions = actionsByView[ActiveView.SEARCH_PRODUCTS];
      return (
        <ProductSearch
          category={ProductCategory.WALLS}
          initialFilter={initialFilter}
          onBack={actions.onBack}
          onVariationSelected={actions.onVariationSelected}
          title={headerText as string}
        />
      );
    })(),
    [ActiveView.VIEW_GROUP_DETAILS]: (() => {
      const actions = actionsByView[ActiveView.VIEW_GROUP_DETAILS];
      return (
        <VariationDetails
          category={ProductCategory.WALLS}
          onApply={actions.onApply}
          onBack={actions.onBack}
          onDifferentColorSelected={actions.onDifferentColorSelected}
          onDifferentSizeSelected={actions.onDifferentSizeSelected}
          variation={activeVariation as Variation}
        />
      );
    })(),
  };

  return viewByMode[viewMode];
};
